import { TimeDao } from '@/dao';
import { AddShootingRequest, AddTimeRequest, DisableTimeRequest, EnableTimeRequest, TimepenaltyRequest } from '@/objects/generated';
import { useEventCommonStore, useEventStoreRef } from '@/stores';
import { addSecondsToDate, dateWithCurrentTime } from '@/utilities/dateutils';

export const useTransactions = () => {
    const { eventID } = useEventStoreRef();

    /**
     * Add a list of times as TIMM transactions
     * @param times
     * @returns IntArray containing the IDs of the raceContestants
     */
    const addTimes = async (times: AddTimeRequest[]) => {
        if (!eventID.value || times.length == 0) {
            return;
        }

        return await TimeDao.AddTimes(eventID.value, times);
    };

    /**
     * Add a single time as a TIMM transaction
     * @param bib
     * @param time
     * @param stationId
     * @returns IntArray containing the IDs of the raceContestants
     */
    const addTime = async (bib: number, time: Date, stationId: number) => {
        return await TimeDao.AddTime(eventID.value ?? 0, bib, stationId, time);
    };

    /**
     * Adds a TIM request
     * @param requests
     * @returns
     */
    const addTIM = async (requests: AddTimeRequest[]) => {
        return await TimeDao.AddTIM(eventID.value ?? 0, requests);
    };

    /**
     * Add a penalty/GPEN transaction
     * @param request
     * @returns
     */
    const addPenalty = async (request: TimepenaltyRequest) => {
        return await TimeDao.AddPenalty(request);
    };

    /**
     * Disable a list of TUIDs
     * @param transactionIds
     * @returns
     */
    const disableTransactions = async (TUIDs: string[]) => {
        const transactions = TUIDs.map((tuid) => {
            const req = DisableTimeRequest.FromJsValues({
                TUID: tuid,
            });
            return req;
        });

        if (!eventID.value || transactions.length == 0) {
            return;
        }

        return await TimeDao.DisableTimes(eventID.value, transactions);
    };

    /**
     * Enable a list of TUIDs
     * @param transactionIds
     * @returns
     */
    const enableTransactions = async (TUIDs: string[]) => {
        const transactions = TUIDs.map((tuid) => {
            const req = EnableTimeRequest.FromJsValues({
                TUID: tuid,
            });
            return req;
        });

        if (!eventID.value || transactions.length == 0) {
            return;
        }

        return await TimeDao.EnableTimes(eventID.value, transactions);
    };

    const addShooting = async (params: {
        raceContestantId: number;
        contestantId: number;
        bib: number;
        lane: number;
        shootingNumber: number;
        shots: Array<number>;
        raceContestantStartTime?: Date | null;
        // TODO: Add shootingTime so the user can set the actual time of the shooting when adding?
    }) => {
        const { raceContestantId, contestantId, bib, lane, shootingNumber, shots, raceContestantStartTime } = params;

        if (shots.length !== 5) {
            throw new Error(`There is always 5 shots in biathlon. Got ${shots.length} shots: ${shots.join(', ')} instead.`);
        }

        let shootingTime;
        // If we have a starttime we can make sure we are after the starttime
        if (raceContestantStartTime) {
            // Shootingtime is 15 seconds after the starttime so we are sure we are after starttime
            // 15 seconds is kinda random but its the same as the shooting offset in ScoringEngine (not that it matters since its overwritten anyway)
            shootingTime = addSecondsToDate(raceContestantStartTime, 15);
        } else {
            // If we dont have a start time we use the event date + current time
            const { Event } = useEventCommonStore();

            if (!Event) {
                throw new Error('Event is not set');
            }

            // Note: this might be before the contestants starttime, in that case this the scoringengine will add it as disabled
            shootingTime = dateWithCurrentTime(Event.Date);
        }

        return await TimeDao.AddShooting(eventID.value, [
            AddShootingRequest.FromJsValues({
                RaceContestantId: raceContestantId,
                ContestantId: contestantId,
                Bib: bib,
                Lane: lane,
                ShootingNumber: shootingNumber,
                Time: shootingTime,
                Shots: shots,
            }),
        ]);
    };

    return {
        DisableTransactions: disableTransactions,
        EnableTransactions: enableTransactions,
        AddTimes: addTimes,
        AddTime: addTime,
        AddPenalty: addPenalty,
        AddShooting: addShooting,
        AddTIM: addTIM,
    };
};
